/* eslint-disable max-lines-per-function */
/**
 * @name CompanyUsersModule
 * @description Module used for retrieving company users.
 * @author Kelvin John Santos
 * @version 1.0
 * @since March 21, 2019
 */

// Proxies
import GetCompanyUserProxy from "../../proxies/back-end/company-user/get";
// Utilities
import LocalStoreManager from "../../utilities/local-store-manager";
import { getAppFeatureProp } from "@/utilities/app-feature-util";

import { SUBSCRIPTION_SERVICE_TYPES } from "../../maps/subscription-service-types";

import { Constants } from "customer-sdk/constants";
const { SLEEK_APP_CACHE_KEY } = Constants.STORE_CONSTANTS;
const { CLEAR_COMPANY_USERS, SET_COMPANY_USERS } = Constants.COMPANY_USERS_CONSTANTS;

// native script
let getCompanyUsers = async (id) => {
  let proxy = new GetCompanyUserProxy();
  let store = new LocalStoreManager();

  return await proxy.execute({ authToken: store.getToken(), id: id });
};

let sleekApp = JSON.parse(localStorage.getItem(SLEEK_APP_CACHE_KEY));

// initial state
function initialState() {
  return {
    companyUsers: sleekApp?.companyUsersModule?.companyUsers ?? [],
    companyBillingTransactions: [],
    companyServices: [],
    subscriptionIds: null,
    selectedCompany: [],
    priceList: [],
    totalPrice: null,
    totalCompanySelected: 0,
    companyIdFromBillingProfile: "",
  };
}

const state = initialState;

// getters
const getters = {
  companyUsers: (state) => state.companyUsers,
  getSelectedCompany: (state) => state.selectedCompany,
  getTotalPrice: (state) => state.totalPrice,
  getCompanyBillingTransactions: (state) => {
    let subscription = state.companyBillingTransactions.filter((item) => item.status !== "inactive");
    return subscription;
  },
  getServices: (state) => state.companyServices,
};

// actions
const actions = {
  // eslint-disable-next-line no-unused-vars
  async GET_COMPANY_USERS({ commit }, payload) {
    return await getCompanyUsers(payload);
  },
  SET_COMPANY_USERS({ commit }, payload) {
    commit(SET_COMPANY_USERS, payload);
  },
  CLEAR_COMPANY_USERS({ commit }, payload) {
    commit(CLEAR_COMPANY_USERS, payload);
  },
  SET_COMPANY_BILLING_TRANSACTION({ commit }, payload) {
    if (payload.length > 0) {
      let filteredArr = payload.filter((item) => item);
      commit("SET_COMPANY_BILLING_TRANSACTION", filteredArr);
    } else {
      commit("CLEAR_COMPANY_BILLING_TRANSACTION", null);
    }
  },
  CLEAR_COMPANY_BILLING_TRANSACTION({ commit }, payload) {
    commit("CLEAR_COMPANY_BILLING_TRANSACTION", payload);
  },
  filterSubscriptionsPerPlatform({ commit }, payload) {
    const MAP_CONSTANTS = getAppFeatureProp(payload.platformConfig.cmsGeneralFeatureList, "MAPS_CONSTANTS");
    const SUBSCRIPTION_STATUSES = getAppFeatureProp(MAP_CONSTANTS.props, "SUBSCRIPTION_STATUSES");

    const { ACTIVE, CHURN_REQUESTED, CHURN_PROCESS, CHURN, STRIKING_OFF_REQUESTED, STRIKING_OFF, STRIKED_OFF } =
      SUBSCRIPTION_STATUSES.value;

    const billing_settings = getAppFeatureProp(payload.platformConfig.cmsAppFeatures, "billing_settings");
    const display_other_subscriptions = getAppFeatureProp(billing_settings.props, "display_other_subscriptions");

    const active_services = getAppFeatureProp(payload.platformConfig.cmsAppFeatures, "active_services");
    const nominee_director = getAppFeatureProp(active_services.props, "nominee_director");

    let filteredSubscriptions = payload.subscriptions.filter(
      // eslint-disable-next-line
      (subscription) =>
        (subscription.service === SUBSCRIPTION_SERVICE_TYPES.NOMINEE_DIRECTOR && nominee_director.enabled) ||
        subscription.service === SUBSCRIPTION_SERVICE_TYPES.SECRETARY ||
        subscription.service === SUBSCRIPTION_SERVICE_TYPES.CORP_SEC_1_SHAREHOLDER ||
        subscription.service === SUBSCRIPTION_SERVICE_TYPES.CORP_SEC_2_SHAREHOLDER ||
        subscription.service === SUBSCRIPTION_SERVICE_TYPES.CORP_SEC_3_5_SHAREHOLDER ||
        subscription.service === SUBSCRIPTION_SERVICE_TYPES.CORP_SEC_6_9_SHAREHOLDER ||
        subscription.service === SUBSCRIPTION_SERVICE_TYPES.CORP_SEC_10_20_SHAREHOLDER ||
        subscription.service === SUBSCRIPTION_SERVICE_TYPES.CORP_SEC_21_30_SHAREHOLDER ||
        subscription.service === SUBSCRIPTION_SERVICE_TYPES.CORP_SEC_31_50_SHAREHOLDER ||
        subscription.service === SUBSCRIPTION_SERVICE_TYPES.ACRA_ANNUAL_FILING_FEE ||
        subscription.service === SUBSCRIPTION_SERVICE_TYPES.MAILROOM ||
        (display_other_subscriptions.enabled &&
          (subscription.service === SUBSCRIPTION_SERVICE_TYPES.ACCOUNTING_XBRL_FILE ||
            subscription.service === SUBSCRIPTION_SERVICE_TYPES.XERO_MULTI_CURRENCY_UPGRADE ||
            subscription.service === SUBSCRIPTION_SERVICE_TYPES.DORMANT_COMPANY ||
            subscription.service === SUBSCRIPTION_SERVICE_TYPES.PAYROLL_PER_STAFF ||
            subscription.service === SUBSCRIPTION_SERVICE_TYPES.GST_QUARTERLY_COMPLIANCE ||
            subscription.service === SUBSCRIPTION_SERVICE_TYPES.SUPREME_QUARTERLY_PLAN ||
            subscription.service === SUBSCRIPTION_SERVICE_TYPES.SUPREME_PLUS_PLAN ||
            subscription.service === SUBSCRIPTION_SERVICE_TYPES.A2X_SHOPIFY_MINI ||
            subscription.service === SUBSCRIPTION_SERVICE_TYPES.A2X_SHOPIFY_BASIC ||
            subscription.service === SUBSCRIPTION_SERVICE_TYPES.A2X_SHOPIFY_STANDARD ||
            subscription.service === SUBSCRIPTION_SERVICE_TYPES.A2X_SHOPIFY_ADVANCED ||
            subscription.service === SUBSCRIPTION_SERVICE_TYPES.A2X_AMAZON_MINI ||
            subscription.service === SUBSCRIPTION_SERVICE_TYPES.A2X_AMAZON_BASIC ||
            subscription.service === SUBSCRIPTION_SERVICE_TYPES.A2X_AMAZON_STANDARD ||
            subscription.service === SUBSCRIPTION_SERVICE_TYPES.A2X_AMAZON_PREMIUM)) ||
        subscription.service === SUBSCRIPTION_SERVICE_TYPES.ACCT_2_500_EXPENSE_ACCOUNTING_PLAN ||
        subscription.service === SUBSCRIPTION_SERVICE_TYPES.ACCT_7_500_EXPENSE_ACCOUNTING_PLAN ||
        subscription.service === SUBSCRIPTION_SERVICE_TYPES.ACCT_15_000_EXPENSE_ACCOUNTING_PLAN ||
        subscription.service === SUBSCRIPTION_SERVICE_TYPES.ACCT_35_000_EXPENSE_ACCOUNTING_PLAN ||
        subscription.service === SUBSCRIPTION_SERVICE_TYPES.ACCT_75_000_EXPENSE_ACCOUNTING_PLAN ||
        subscription.service === SUBSCRIPTION_SERVICE_TYPES.ACCT_125_000_EXPENSE_ACCOUNTING_PLAN ||
        subscription.service === SUBSCRIPTION_SERVICE_TYPES.ACCT_175_000_EXPENSE_ACCOUNTING_PLAN ||
        subscription.service === SUBSCRIPTION_SERVICE_TYPES.ACCT_250_000_EXPENSE_ACCOUNTING_PLAN ||
        subscription.service === SUBSCRIPTION_SERVICE_TYPES.ACCT_325_000_EXPENSE_ACCOUNTING_PLAN ||
        subscription.service === SUBSCRIPTION_SERVICE_TYPES.GST_ANNUAL_COMPLIANCE ||
        ((subscription.service === SUBSCRIPTION_SERVICE_TYPES.ACCOUNTING_SLIM ||
          subscription.service === SUBSCRIPTION_SERVICE_TYPES.ACCOUNTING_STARTUP ||
          subscription.service === SUBSCRIPTION_SERVICE_TYPES.ACCOUNTING_SME ||
          subscription.service === SUBSCRIPTION_SERVICE_TYPES.ACCOUNTING_SUPREME) &&
          [ACTIVE, CHURN_REQUESTED, CHURN_PROCESS, CHURN, STRIKING_OFF_REQUESTED, STRIKING_OFF, STRIKED_OFF].includes(
            subscription.status
          ))
    );
    commit("SET_COMPANY_BILLING_TRANSACTION", filteredSubscriptions);
  },
  onChangedSelected({ commit, dispatch }, payload) {
    commit("setSelectedCompany", payload);
    dispatch("updatePriceList", null);
    dispatch("totalSelectedListener", null);
  },
  updatePriceList({ state, commit }) {
    let priceList = state.selectedCompany.map((item) => {
      if (item.service !== "director") {
        let price = state.priceList.find((elem) => elem.service === item.service);
        return price;
      }

      if (item.service === "director") {
        let price = state.priceList.find((elem) => {
          if (elem.duration === item.duration && elem.service === "director") {
            return elem;
          }
        });
        return price;
      }
    });

    let filterPrice = priceList.map((item) => item.price);
    const totalPrice = filterPrice.reduce((accumulator, currentValue) => accumulator + currentValue, 0);
    commit("setTotalPrice", totalPrice);
  },
  setSelectAll({ dispatch, commit }, payload) {
    let data = payload.arr;
    let total = data.length;
    commit("setSelectAll", payload);
    commit("setTotalSelected", total);
    dispatch("totalSelectedListener", null);
    dispatch("updatePriceList", null);
  },
  onUpdateNomineeDirectorSubscription({ state, commit, dispatch }, payload) {
    let data = state.companyBillingTransactions.map((item) => {
      if (item._id === payload.id) {
        item.duration = payload.duration;
      }
      return item;
    });
    dispatch("updatePriceList", { duration: payload.duration });
    commit("SET_COMPANY_BILLING_TRANSACTION", data);
  },
  totalSelectedListener() {},
  updateCompanyDeleteIcon() {},
  hideShoppingCartAfterPayment() {},
  hideShoppingCartDropdown() {},
  redirectToOnboardingSteps() {},
  redirectToPaymentPage() {},
};

// mutations
const mutations = {
  SET_COMPANY_USERS(state, payload) {
    state.companyUsers = payload;
  },
  // eslint-disable-next-line no-unused-vars
  CLEAR_COMPANY_USERS(state, payload) {
    state.companyUsers = [];
  },
  SET_COMPANY_SERVICES(state, payload) {
    state.companyServices = payload;
  },
  CLEAR_COMPANY_SERVICES(state, payload) {
    state.companyServices = payload;
  },
  //@ Transaction mutations
  SET_COMPANY_BILLING_TRANSACTION(state, payload) {
    state.companyBillingTransactions = payload;
  },
  CLEAR_COMPANY_BILLING_TRANSACTION(state) {
    state.companyBillingTransactions = [];
  },
  setSubscriptionIds(state, payload) {
    if (payload) {
      state.subscriptionIds = payload.subscriptionIds;
      state.companyIdFromBillingProfile = payload.companyIdFromBillingProfile;
    }
  },
  setSelectedCompany(state, payload) {
    if (payload.selected) {
      state.selectedCompany.push(payload.item);
    } else {
      let index = state.selectedCompany.findIndex((item) => item._id === payload.item._id);
      if (index !== -1) state.selectedCompany.splice(index, 1);
    }
    state.totalCompanySelected = state.selectedCompany.length;
  },
  setSelectAll(state, payload) {
    state.selectedCompany = payload.arr;
  },
  setTotalSelected(state, payload) {
    state.totalCompanySelected = payload;
  },
  setPriceList(state, payload) {
    state.priceList = payload;
  },
  setTotalPrice(state, payload) {
    state.totalPrice = payload;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
